import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../lib/store-auth';
import './sales-coach-bot.scss';

interface Message {
    role: 'user' | 'ai';
    text: string;
}

interface SalesCoachBotProps {
    embedded?: boolean;
    targetUserId?: string;
    targetUserName?: string;
}

export const SalesCoachBot: React.FC<SalesCoachBotProps> = ({
    embedded = false,
    targetUserId,
    targetUserName
}) => {
    const { user } = useAuthStore();
    const [isOpen, setIsOpen] = useState(embedded); // Open by default if embedded
    const [showGreeting, setShowGreeting] = useState(!embedded);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [context, setContext] = useState<string>('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const loadContext = useCallback(async () => {
        if (!user || !supabase) return;

        // Use targetUserId if provided (Manager interacting with personnel data), otherwise use current user
        const userIdToFetch = targetUserId || user.id;
        const userNameToUse = targetUserName || user.full_name;

        try {
            // Fetch last 5 reports
            const { data: reports, error } = await supabase
                .from('simulation_reports')
                .select('simulation_title, score, report_data')
                .eq('user_id', userIdToFetch)
                .order('created_at', { ascending: false })
                .limit(5);

            if (error) throw error;

            if (reports && reports.length > 0) {
                const reportSummaries = reports.map((r, i) => {
                    const data = typeof r.report_data === 'string' ? JSON.parse(r.report_data) : r.report_data;
                    return `
Simülasyon ${i + 1}: ${r.simulation_title}
Puan: ${r.score}
Artılar: ${data.positives?.join(', ')}
Eksiler: ${data.negatives?.join(', ')}
Genel Yorum: ${data.shortAnalysis}
                    `.trim();
                }).join('\n---\n');

                setContext(`
Kullanıcı Adı: ${userNameToUse}
${targetUserId ? '(Sen şu an bu personelin yöneticisisin ve personeli analiz ediyorsun)' : ''}
Son 5 Simülasyon Özeti:
${reportSummaries}

Sen deneyimli, yapıcı ve motive edici bir satış koçusun.
GÖREVİN: ${targetUserId ? 'Yöneticiye, bu personelin performansı hakkında bilgi vermek.' : 'Kullanıcının yukarıdaki verilerine dayanarak sorularına cevap vermek.'}
ÖNEMLİ KURALLAR:
1. ${targetUserId ? 'Yöneticiye personel hakkında objektif ve gelişim odaklı analizler sun.' : 'Kullanıcı senden tavsiye istemedikçe veya analiz sormadıkça SAKIN tavsiye verme.'}
2. "Merhaba", "Nasılsın" gibi giriş cümlelerine sadece sıcak bir karşılık ver ve "Sana nasıl yardımcı olabilirim?" diye sor.
3. Sadece kullanıcı açıkça "Eksiklerim neler?", "Nasıl daha iyi satarım?", "Analiz et" gibi şeyler sorduğunda yukarıdaki verileri kullan ve detaylı tavsiye ver.
4. KONU SINIRLAMASI: Sadece satış, ikna, iletişim becerileri ve performans hakkındaki soruları cevapla. Satışla ilgisi olmayan (örneğin tarih, genel kültür, kodlama vb.) sorulara nazikçe "Ben sadece satış koçluğu yapabilirim." de.
5. Kısa ve öz cevaplar ver.
                `.trim());
            } else {
                setContext(`Kullanıcı Adı: ${userNameToUse}. Henüz hiç raporu yok. Yeni başladığını varsay. Kullanıcı tavsiye isterse genel satış tavsiyeleri ver. Sadece satış konularında konuş, başka konuları nazikçe reddet.`);
            }

        } catch (err) {
            console.error("Context loading error:", err);
        }
    }, [user, setContext, targetUserId, targetUserName]);

    // Initial Greeting and Context Loading
    useEffect(() => {
        // If embedded, load context immediately. If not, wait for open.
        if ((isOpen || embedded) && messages.length === 0) {
            const greeting = targetUserId
                ? `Merhaba! ${targetUserName} hakkında ne bilmek istersiniz?`
                : `Merhaba ${user?.full_name?.split(' ')[0] || ''}! Ben Satış Koçun. Nasıl yardımcı olabilirim?`;

            setMessages([
                { role: 'ai', text: greeting }
            ]);
            loadContext();
        }
    }, [isOpen, embedded, messages.length, user?.full_name, loadContext, targetUserId, targetUserName]);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        if (!inputText.trim() || isLoading) return;

        const userMsg = inputText.trim();
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setInputText('');
        setIsLoading(true);

        try {
            // Context and Prompt
            const prompt = `
SİSTEM: ${context}

GEÇMİŞ MESAJLAR:
${messages.map(m => `${m.role === 'user' ? 'KULLANICI' : 'KOÇ'}: ${m.text}`).join('\n')}
KULLANICI: ${userMsg}
KOÇ:
            `.trim();

            let replyText = "";
            let success = false;

            // 1. Try Serverless API (Production)
            try {
                const response = await fetch('/api/gemini', {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        prompt,
                        config: {
                            responseMimeType: "text/plain",
                            temperature: 0.7,
                            maxOutputTokens: 1000,
                        }
                    }),
                });

                if (response.ok && !response.headers.get("content-type")?.includes("text/html")) {
                    const data = await response.json();
                    if (data.result) {
                        replyText = data.result;
                        success = true;
                    }
                }
            } catch (ignore) {
                // Serverless failed, continue to fallback
            }

            // 2. Fallback to Direct API (Local/Development)
            if (!success) {
                const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

                if (!apiKey) {
                    throw new Error("API yapılandırma hatası: Anahtar bulunamadı.");
                }

                const response = await fetch(
                    `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${apiKey}`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            contents: [{ parts: [{ text: prompt }] }],
                            generationConfig: {
                                temperature: 0.7,
                                maxOutputTokens: 1000,
                            }
                        }),
                    }
                );

                if (!response.ok) throw new Error("API hatası");

                const data = await response.json();
                replyText = data.candidates?.[0]?.content?.parts?.[0]?.text || "";
            }

            if (replyText) {
                setMessages(prev => [...prev, { role: 'ai', text: replyText }]);
            } else {
                setMessages(prev => [...prev, { role: 'ai', text: "Üzgünüm, cevap veremiyorum." }]);
            }

        } catch (err) {
            console.error("Chat error:", err);
            setMessages(prev => [...prev, { role: 'ai', text: "Bağlantı hatası oluştu." }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSend();
    };

    // Render for Embedded Mode
    if (embedded) {
        return (
            <div className="sales-coach-bot embedded" style={{ width: '100%', height: '100%' }}>
                <div className="chat-window" style={{ position: 'static', width: '100%', height: '100%', borderRadius: 0, boxShadow: 'none', display: 'flex', flexDirection: 'column' }}>
                    <div className="messages-list" style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`message-bubble ${msg.role}`} style={{ maxWidth: '85%' }}>
                                {msg.text}
                            </div>
                        ))}
                        {isLoading && (
                            <div className="typing-indicator">
                                <span></span><span></span><span></span>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="input-area" style={{ padding: '12px', borderTop: '1px solid #e5e7eb' }}>
                        <input
                            type="text"
                            placeholder="Soru sor..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            style={{ fontSize: '14px' }}
                        />
                        <button onClick={handleSend} disabled={!inputText.trim() || isLoading}>
                            <span className="material-symbols-outlined">send</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Render for Floating Mode (Original)
    return (
        <div className="sales-coach-bot">
            {showGreeting && !isOpen && (
                <div className="greeting-bubble">
                    Merhaba, hoş geldin! 👋
                </div>
            )}
            {isOpen && (
                <div className="chat-window">
                    <div className="chat-header">
                        <div className="bot-avatar">
                            <img src="/sato.png" alt="SaTo" />
                        </div>
                        <div className="header-info">
                            <h3>SaTo</h3>
                            <p>Kişisel Satış Koçun</p>
                        </div>
                        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <div className="messages-list">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`message-bubble ${msg.role}`}>
                                {msg.text}
                            </div>
                        ))}
                        {isLoading && (
                            <div className="typing-indicator">
                                <span></span><span></span><span></span>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="input-area">
                        <input
                            type="text"
                            placeholder="Bir şeyler yaz..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <button onClick={handleSend} disabled={!inputText.trim() || isLoading}>
                            <span className="material-symbols-outlined">send</span>
                        </button>
                    </div>
                </div>
            )}

            <button className={`fab-button ${isOpen ? 'open' : ''}`} onClick={() => {
                setIsOpen(!isOpen);
                setShowGreeting(false);
            }}>
                {isOpen ? (
                    <span className="material-symbols-outlined">close</span>
                ) : (
                    <img src="/sato.png" alt="SaTo" className="fab-icon" />
                )}
            </button>
        </div>
    );
};
